import type { ReactNode } from 'react'
import { useState } from 'react'

import { Menu, NumberInput, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import Tag from 'src/components/DataDisplay/Tag/Tag'

import { TASK_MENU_CLASSNAMES } from './taskMenuStyles'

interface TaskOffsetMenuProps {
  // Offset is stored in hours; `undefined` means no default offset.
  value: number | undefined
  setValue: (hours: number | undefined) => void
  icon: ReactNode
  // Tooltip + empty-state label, e.g. 'Default due' / 'Default reminder'.
  label: string
  testId: string
}

type CustomUnit = 'hours' | 'days'

const HOURS_PER_DAY = 24

const formatHours = (hours: number) => {
  if (hours % HOURS_PER_DAY === 0) {
    const days = hours / HOURS_PER_DAY
    return `${days} day${days === 1 ? '' : 's'}`
  }

  return `${hours} hour${hours === 1 ? '' : 's'}`
}

const PREDEFINED_OPTIONS = [
  { label: '2 hours', value: 2 },
  { label: '4 hours', value: 4 },
  { label: '1 day', value: 24 },
  { label: '2 days', value: 48 },
  { label: '1 week', value: 168 },
  { label: '1 month', value: 720 },
  { label: '3 months', value: 2160 },
]

// Shared dropdown for a task template's default due/reminder offset. Stores the
// offset in hours so recruiters can set sub-day "call back" reminders, while
// still surfacing day-friendly presets and a custom day entry.
const TaskOffsetMenu = ({
  value,
  setValue,
  icon,
  label,
  testId,
}: TaskOffsetMenuProps) => {
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customValue, setCustomValue] = useState<number | undefined>(undefined)
  const [customUnit, setCustomUnit] = useState<CustomUnit>('hours')
  const [opened, { open, close }] = useDisclosure(false)

  const getDisplay = () => {
    if (!value) return label

    const predefined = PREDEFINED_OPTIONS.find(
      (option) => option.value === value
    )
    return predefined ? predefined.label : formatHours(value)
  }

  const handlePredefinedSelect = (hours: number) => {
    setValue(hours)
    setShowCustomInput(false)
    setCustomValue(undefined)
    close()
  }

  const handleCustomSelect = (unit: CustomUnit) => {
    setCustomUnit(unit)
    setShowCustomInput(true)
    // Prefill the input from the current offset when it can be represented in
    // the chosen unit; days only prefill for whole-day offsets.
    if (!value) {
      setCustomValue(undefined)
    } else if (unit === 'hours') {
      setCustomValue(value)
    } else {
      setCustomValue(
        value % HOURS_PER_DAY === 0 ? value / HOURS_PER_DAY : undefined
      )
    }
  }

  const handleCustomSubmit = () => {
    if (customValue && customValue > 0) {
      setValue(
        customUnit === 'days' ? customValue * HOURS_PER_DAY : customValue
      )
    }
    setShowCustomInput(false)
    close()
  }

  const handleClear = () => {
    setValue(undefined)
    setShowCustomInput(false)
    setCustomValue(undefined)
    close()
  }

  return (
    <Menu
      offset={6}
      width={185}
      position="bottom-start"
      closeOnItemClick={false}
      opened={opened}
      onOpen={open}
      onClose={() => {
        close()
        setShowCustomInput(false)
        setCustomValue(undefined)
      }}
      classNames={TASK_MENU_CLASSNAMES}
    >
      <Tooltip label={label} position="bottom-start" className="text-xs">
        <Menu.Target>
          <div>
            <Tag
              data-testid={testId}
              showCloseButton={!!value}
              size="xs"
              clickable
              onClose={() => {
                handleClear()
              }}
            >
              {icon}
              {getDisplay()}
            </Tag>
          </div>
        </Menu.Target>
      </Tooltip>

      <Menu.Dropdown>
        {!showCustomInput ? (
          <>
            {PREDEFINED_OPTIONS.map((option) => (
              <Menu.Item
                key={option.value}
                className={
                  value === option.value ? 'bg-doubleNickel-gray-100' : ''
                }
                onClick={() => handlePredefinedSelect(option.value)}
              >
                {option.label}
              </Menu.Item>
            ))}
            <Menu.Item onClick={() => handleCustomSelect('hours')}>
              Custom hours
            </Menu.Item>
            <Menu.Item onClick={() => handleCustomSelect('days')}>
              Custom days
            </Menu.Item>
            {value && <Menu.Item onClick={handleClear}>Clear</Menu.Item>}
          </>
        ) : (
          <div className="flex items-center gap-2 p-2">
            <NumberInput
              placeholder={`Enter ${customUnit}`}
              value={customValue}
              onChange={(inputValue) => {
                const numericValue =
                  typeof inputValue === 'number'
                    ? inputValue
                    : Number(inputValue)
                setCustomValue(
                  Number.isFinite(numericValue) ? numericValue : undefined
                )
              }}
              min={1}
              allowDecimal={false}
              size="xs"
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCustomSubmit()
                }
              }}
            />
            <button
              onClick={handleCustomSubmit}
              className="text-doubleNickel-primary-500 hover:text-doubleNickel-primary-600 border-doubleNickel-primary-200 hover:bg-doubleNickel-primary-50 rounded border px-2 py-1 text-xs"
            >
              Set
            </button>
          </div>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

export default TaskOffsetMenu
